// As últimas propostas decididas (ganhas e perdidas), com três colunas preenchidas à mão:
// situação que levou o cliente, reunião de diagnóstico antes da proposta e por que fechou/perdeu.
// As anotações ficam gravadas no PRÓPRIO NEGÓCIO (o servidor grava no negócio e deixa rastro).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App as AntApp, Button, Input, Select, Skeleton, Space, Table, Tag, Typography } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  conteudoPropostas, ehConflito, ehSemAcesso, erroDe, salvarLinhaProposta, type CamposLinha, type Diagnostico, type LinhaProposta,
} from '../api';
import { CampoData } from '../comum';
import {
  camposAlterados, DIAGNOSTICOS, dia, errosDaLinha, LIMITE_TEXTO, linhaCompleta, resumoPropostas, separarMotivoPerda,
} from '../regras';
import type { PropsPagina } from './tipos';
import { fmtDataBr } from '../../../utils/dataFmt';
import { canalLabel } from '../../comercial-leads/utils';
import { MOTIVOS_DE_PERDA, textoDoMotivo } from '../../negocio-detalhe/ModalPerder';
import { pedirAberturaDoNegocio } from '../../atividades/utils';

const { Text } = Typography;

const brl = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const OPCOES_DIAG = DIAGNOSTICOS.map((d) => ({ value: d.value, label: d.label }));
const OPCOES_MOTIVO = MOTIVOS_DE_PERDA.map((m) => ({ value: m, label: m }));

type Edicao = Partial<CamposLinha>;

export default function Propostas20({ tarefa, podeVer, podeEditar }: PropsPagina) {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const [linhas, setLinhas] = useState<LinhaProposta[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);
  const [edicoes, setEdicoes] = useState<Record<number, Edicao>>({});
  const [salvando, setSalvando] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLinhas(await conteudoPropostas(tarefa.id));
      setErro(null);
    } catch (e) {
      if (ehSemAcesso(e)) setSemAcesso(true); else setErro(erroDe(e));
    }
  }, [tarefa.id]);

  useEffect(() => { if (podeVer) void carregar(); }, [podeVer, carregar]);

  const resumo = useMemo(() => {
    if (!linhas) return null;
    // O resumo conta o que já está GRAVADO, não o rascunho da tela.
    return resumoPropostas(linhas);
  }, [linhas]);

  if (!podeVer || semAcesso) {
    return <Alert type="warning" showIcon message="Sem acesso" description="Esta página mostra propostas do comercial e é só para quem pode ver o pipeline." />;
  }
  if (erro) return <Alert type="error" showIcon message="Não foi possível carregar as propostas" description={erro} action={<Button size="small" onClick={() => void carregar()}>Tentar de novo</Button>} />;
  if (!linhas) return <Skeleton active />;
  if (!linhas.length) return <Alert type="info" showIcon message="Nenhuma proposta decidida encontrada." />;

  const valorDe = (l: LinhaProposta): LinhaProposta => ({ ...l, ...(edicoes[l.id] || {}) });
  const mudar = (id: number, patch: Edicao) => setEdicoes((e) => ({ ...e, [id]: { ...(e[id] || {}), ...patch } }));
  const descartar = (id: number) => setEdicoes((e) => { const n = { ...e }; delete n[id]; return n; });

  const escolherDiagnostico = (id: number, v: Diagnostico | undefined) => {
    if (!v) mudar(id, { diagnostico: null, diagnostico_data: null, diagnostico_motivo: null });
    else if (v === 'houve') mudar(id, { diagnostico: v, diagnostico_motivo: null });
    else mudar(id, { diagnostico: v, diagnostico_data: null });
  };

  const salvar = async (base: LinhaProposta) => {
    const campos = camposAlterados(base, edicoes[base.id] || {});
    const chaves = Object.keys(campos) as (keyof CamposLinha)[];
    if (!chaves.length) { descartar(base.id); return; }
    const erros = errosDaLinha(campos, base);
    if (erros.length) { message.warning(erros[0]); return; }
    const vistos: Partial<CamposLinha> = {};
    for (const k of chaves) (vistos as Record<string, unknown>)[k] = base[k] ?? null;
    setSalvando(base.id);
    try {
      const nova = await salvarLinhaProposta(tarefa.id, base.id, campos, vistos);
      descartar(base.id);
      message.success('Anotação gravada no negócio.');
      if (nova && nova.id === base.id) setLinhas((ls) => (ls || []).map((x) => (x.id === base.id ? { ...x, ...nova } : x)));
      else await carregar();
    } catch (e) {
      if (ehConflito(e)) {
        message.warning('Outra pessoa mexeu neste negócio antes de você. Atualizei a lista; confira e anote de novo.');
        descartar(base.id);
        await carregar();
      } else {
        message.error(erroDe(e));
      }
    } finally {
      setSalvando(null);
    }
  };

  const abrirNegocio = (id: number) => {
    if (pedirAberturaDoNegocio('negocio', String(id))) navigate('/comercial/pipeline');
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {resumo && (
        <Space size={[24, 4]} wrap>
          <Text><b>{resumo.completas}</b> de {resumo.total} completas</Text>
          <Text>Com reunião de diagnóstico: <b>{resumo.comReuniao.n}</b> ({resumo.comReuniao.ganhas} ganhas)</Text>
          <Text>Sem reunião: <b>{resumo.semReuniao.n}</b> ({resumo.semReuniao.ganhas} ganhas)</Text>
          {resumo.dispensadas > 0 && <Text type="secondary">{resumo.dispensadas} dispensadas</Text>}
        </Space>
      )}
      {!podeEditar && <Alert type="info" showIcon message="Você pode ver as propostas, mas não anotar nelas." />}

      <Table<LinhaProposta>
        size="small" pagination={false} rowKey="id" dataSource={linhas} scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Cliente', width: 200,
            render: (_, l) => (<div><div>{l.cliente || '—'}</div>{l.segmento && <Text type="secondary" style={{ fontSize: 12 }}>{l.segmento}</Text>}</div>),
          },
          {
            title: 'Proposta', width: 240,
            render: (_, l) => (<div><div>{l.titulo}</div><Text strong>{brl(l.valor)}</Text></div>),
          },
          {
            title: 'Enviada → decidida', width: 190,
            render: (_, l) => {
              const motivo = l.status === 'ganho' ? l.motivo_ganho : l.motivo_perda;
              return (
                <div>
                  <div>{fmtDataBr(l.data_envio)} → {fmtDataBr(l.data_fechamento)}</div>
                  <Tag color={l.status === 'ganho' ? 'green' : 'red'}>{l.status === 'ganho' ? 'Ganha' : 'Perdida'}</Tag>
                  {motivo && <div><Text type="secondary" style={{ fontSize: 12 }}>{motivo}</Text></div>}
                </div>
              );
            },
          },
          {
            title: 'Responsável', width: 160,
            render: (_, l) => (<div><div>{l.responsavel_nome || '—'}</div>{canalLabel(l.canal) && <Text type="secondary" style={{ fontSize: 12 }}>{canalLabel(l.canal)}</Text>}</div>),
          },
          {
            title: 'Situação que levou o cliente', width: 260,
            render: (_, l) => {
              const v = valorDe(l);
              return (
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 4 }} maxLength={LIMITE_TEXTO} value={v.situacao_cliente || ''} disabled={!podeEditar}
                  placeholder="O que fez o cliente procurar a Hydrodata?" onChange={(ev) => mudar(l.id, { situacao_cliente: ev.target.value })}
                />
              );
            },
          },
          {
            title: 'Reunião de diagnóstico antes da proposta', width: 300,
            render: (_, l) => {
              const v = valorDe(l);
              const sugestao = l.reunioes_antes > 0 && v.diagnostico !== 'houve';
              return (
                <div style={{ display: 'grid', gap: 4 }}>
                  <Select
                    size="small" showSearch optionFilterProp="label" allowClear options={OPCOES_DIAG} value={v.diagnostico || undefined}
                    placeholder="Houve reunião?" disabled={!podeEditar} onChange={(x) => escolherDiagnostico(l.id, x as Diagnostico | undefined)}
                  />
                  {v.diagnostico === 'houve' && (
                    <CampoData valor={v.diagnostico_data} onChange={(d) => mudar(l.id, { diagnostico_data: d })} placeholder="Data da reunião" />
                  )}
                  {(v.diagnostico === 'nao_houve' || v.diagnostico === 'dispensada') && (
                    <Input
                      size="small" maxLength={LIMITE_TEXTO} disabled={!podeEditar} value={v.diagnostico_motivo || ''}
                      placeholder={v.diagnostico === 'dispensada' ? 'Por que foi dispensada?' : 'Por que não houve? (opcional)'}
                      onChange={(ev) => mudar(l.id, { diagnostico_motivo: ev.target.value })}
                    />
                  )}
                  {sugestao && podeEditar && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <BulbOutlined /> {l.reunioes_antes === 1 ? '1 reunião registrada' : `${l.reunioes_antes} reuniões registradas`} antes do envio
                      {l.ultima_reuniao_antes ? `, a última em ${fmtDataBr(l.ultima_reuniao_antes)}` : ''}.{' '}
                      <Button
                        type="link" size="small" style={{ padding: 0 }}
                        onClick={() => mudar(l.id, { diagnostico: 'houve', diagnostico_motivo: null, diagnostico_data: dia(l.ultima_reuniao_antes) })}
                      >usar</Button>
                    </Text>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Por que fechou / perdeu', width: 300,
            render: (_, l) => {
              const v = valorDe(l);
              if (l.status === 'ganho') {
                return (
                  <Input
                    size="small" maxLength={LIMITE_TEXTO} disabled={!podeEditar} value={v.motivo_ganho || ''}
                    placeholder="Por que o cliente fechou?" onChange={(ev) => mudar(l.id, { motivo_ganho: ev.target.value })}
                  />
                );
              }
              const sep = separarMotivoPerda(v.motivo_perda, MOTIVOS_DE_PERDA);
              return (
                <div style={{ display: 'grid', gap: 4 }}>
                  <Select
                    size="small" showSearch optionFilterProp="label" options={OPCOES_MOTIVO} value={sep.motivo || undefined} disabled={!podeEditar}
                    placeholder="Motivo da perda" onChange={(m) => mudar(l.id, { motivo_perda: textoDoMotivo(m, sep.detalhe) })}
                  />
                  <Input
                    size="small" maxLength={LIMITE_TEXTO} disabled={!podeEditar || !sep.motivo} value={sep.detalhe}
                    placeholder={sep.motivo === 'Outro motivo' ? 'Escreva o motivo' : 'Detalhe (opcional)'}
                    onChange={(ev) => mudar(l.id, { motivo_perda: textoDoMotivo(sep.motivo, ev.target.value) })}
                  />
                </div>
              );
            },
          },
          {
            title: '', width: 170, fixed: 'right',
            render: (_, l) => {
              const mexeu = Object.keys(camposAlterados(l, edicoes[l.id] || {})).length > 0;
              return (
                <div style={{ display: 'grid', gap: 4 }}>
                  {podeEditar && mexeu && (
                    <Space size={4}>
                      <Button size="small" type="primary" loading={salvando === l.id} onClick={() => void salvar(l)}>Salvar</Button>
                      <Button size="small" disabled={salvando === l.id} onClick={() => descartar(l.id)}>Descartar</Button>
                    </Space>
                  )}
                  {!mexeu && linhaCompleta(l) && <Tag color="green" style={{ width: 'fit-content' }}>Completa</Tag>}
                  <Button type="link" size="small" style={{ padding: 0, width: 'fit-content' }} onClick={() => abrirNegocio(l.id)}>Abrir negócio</Button>
                </div>
              );
            },
          },
        ]}
      />
    </div>
  );
}
